import * as go from 'gojs';

import {
  LinkCategory,
  LinkLabelCategory,
} from 'src/containers/ProjectDiagram/constants/categories';
import { DiagramTransaction } from 'src/containers/ProjectDiagram/constants/transactions';
import {
  Diagram,
  ModelConceptRelationshipLabelNode,
  ModelConceptRelationshipLink,
} from 'src/containers/ProjectDiagram/types/diagram';
import { commitDiagramWhileSkippingUndoManager } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import {
  isLink,
  isLinkLabel,
} from 'src/containers/ProjectDiagram/utils/graphObject';
import { setDiagramSelection } from 'src/containers/ProjectDiagram/utils/setDiagramSelection';
import { setModelSelection } from 'src/containers/ProjectDiagram/utils/setModelSelection';

const addPartToSelection = (
  part: go.Part,
  selection: go.Set<go.Part>,
  newSelection: go.Set<go.Part>
) => {
  if (!selection.has(part)) {
    newSelection.add(part);
  }
};

const addLinkToSelection = (
  link: go.Link,
  selection: go.Set<go.Part>,
  newSelection: go.Set<go.Part>
) => {
  const linkLabel = link.labelNodes.first();
  addPartToSelection(link, selection, newSelection);
  if (linkLabel) {
    addPartToSelection(linkLabel, selection, newSelection);
  }
};

const addLinkLabelToSelection = (
  linkLabel: go.Node,
  selection: go.Set<go.Part>,
  newSelection: go.Set<go.Part>
) => {
  addPartToSelection(linkLabel, selection, newSelection);
  const link = linkLabel.labeledLink;
  if (link) {
    addPartToSelection(link, selection, newSelection);
  }
};

const addRelationshipToSelection = (
  link: ModelConceptRelationshipLink,
  diagram: go.Diagram,
  selection: go.Set<go.Part>,
  newSelection: go.Set<go.Part>
) => {
  const isUnconnected =
    link.data.to === undefined || link.data.from === undefined;

  if (isUnconnected) {
    addLinkToSelection(link, selection, newSelection);
  } else {
    diagram
      .findLinksByExample({
        value: link.data.value,
        category: LinkCategory.MODEL_CONCEPT_RELATIONSHIP,
      })
      .each((similarLink) => {
        addLinkToSelection(similarLink, selection, newSelection);
      });
  }
};

export const diagramChangedSelection =
  (callback?: go.DiagramEventHandler): go.DiagramEventHandler =>
  (e) => {
    const diagram = e.diagram as Diagram;
    const selection = e.subject as go.Set<go.Part>;
    const newSelection = new go.Set<go.Part>();

    selection.each((part) => {
      if (isLink(part)) {
        addLinkToSelection(part, selection, newSelection);
      } else if (isLinkLabel(part)) {
        addLinkLabelToSelection(part, selection, newSelection);
      }

      if (
        isLink<ModelConceptRelationshipLink>(
          part,
          LinkCategory.MODEL_CONCEPT_RELATIONSHIP
        )
      ) {
        addRelationshipToSelection(part, diagram, selection, newSelection);
      } else if (
        isLinkLabel<ModelConceptRelationshipLabelNode>(
          part,
          LinkLabelCategory.MODEL_CONCEPT_RELATIONSHIP_LABEL
        ) &&
        part.labeledLink
      ) {
        addRelationshipToSelection(
          part.labeledLink,
          diagram,
          selection,
          newSelection
        );
      }
    });

    if (newSelection.size) {
      commitDiagramWhileSkippingUndoManager(
        diagram,
        (_, diagram) =>
          setDiagramSelection(newSelection.addAll(selection), true)(diagram),
        DiagramTransaction.SET_DIAGRAM_SELECTION
      );
    } else {
      diagram.updateSelectionHistory();
      commitDiagramWhileSkippingUndoManager(
        diagram,
        setModelSelection(selection),
        DiagramTransaction.SET_MODEL_SELECTION
      );
    }

    if (callback) {
      callback(e);
    }
  };
